import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header/Header';
import { trackReservation, trackReservationStart, trackMenuClick } from '../utils/analytics';
import { sendReservationEmail } from '../utils/emailService';
import { collection, addDoc, Timestamp, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import './ReservationPage.css';

// Formater une date en YYYY-MM-DD en heure LOCALE (évite le décalage 00h-01h → jour précédent en UTC)
const toLocalDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ReservationPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  // Obtenir la date d'aujourd'hui au format YYYY-MM-DD (heure locale)
  const getTodayDate = () => toLocalDateString(new Date());

  // Générer les dates pour les 30 prochains jours
  const generateDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  // Générer les heures par incréments de 30 minutes (12:00 à 21:30)
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 12; hour <= 21; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(timeString);
      }
    }
    return slots;
  };

  const dates = generateDates();
  const timeSlots = generateTimeSlots();

  const [formData, setFormData] = useState({
    name: '',
    emailOrPhone: '',
    date: getTodayDate(),
    time: '',
    persons: '2',
    note: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reservationData, setReservationData] = useState(null);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [blockedMessage, setBlockedMessage] = useState('');

  // Tracker l'ouverture du formulaire de réservation
  useEffect(() => {
    trackReservationStart();
  }, []);

  // Charger les créneaux bloqués depuis Firestore
  useEffect(() => {
    const loadBlockedSlots = async () => {
      try {
        const blockedSlotsRef = collection(db, 'blockedSlots');
        const querySnapshot = await getDocs(blockedSlotsRef);
        
        const slots = [];
        querySnapshot.forEach((docSnap) => {
          slots.push({
            id: docSnap.id,
            ...docSnap.data()
          });
        });
        
        setBlockedSlots(slots);
      } catch (error) {
        console.error('Erreur lors du chargement des créneaux bloqués:', error);
      }
    };

    loadBlockedSlots();
  }, []);

  // Convertir une heure en minutes pour la comparaison
  const timeToMinutes = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Vérifier si un créneau est bloqué (supporte les plages d'heures)
  const isSlotBlocked = (date, time) => {
    return blockedSlots.some(slot => {
      const slotDateFrom = slot.dateFrom || slot.date;
      const slotDateTo = slot.dateTo || slot.date;
      if (!slotDateFrom || !slotDateTo) return false;
      if (date < slotDateFrom || date > slotDateTo) return false;
      
      const timeMin = timeToMinutes(time);
      
      // Si l'ancien format (time seul) existe (rétrocompatibilité)
      if (slot.time && !slot.timeFrom) {
        return timeMin === timeToMinutes(slot.time);
      }
      
      // Si c'est une plage d'heures
      if (slot.timeFrom && slot.timeTo) {
        const fromMin = timeToMinutes(slot.timeFrom);
        const toMin = timeToMinutes(slot.timeTo);
        return timeMin >= fromMin && timeMin < toMin;
      }
      
      return false;
    });
  };

  // Obtenir le message pour un créneau bloqué
  const getBlockedMessage = (date, time) => {
    const timeMin = timeToMinutes(time);
    
    const slot = blockedSlots.find(s => {
      const slotDateFrom = s.dateFrom || s.date;
      const slotDateTo = s.dateTo || s.date;
      if (!slotDateFrom || !slotDateTo) return false;
      if (date < slotDateFrom || date > slotDateTo) return false;
      
      // Si l'ancien format (time seul) existe
      if (s.time && !s.timeFrom) {
        return timeMin === timeToMinutes(s.time);
      }
      
      // Si c'est une plage d'heures
      if (s.timeFrom && s.timeTo) {
        const fromMin = timeToMinutes(s.timeFrom);
        const toMin = timeToMinutes(s.timeTo);
        return timeMin >= fromMin && timeMin < toMin;
      }
      
      return false;
    });
    
    if (!slot) return '';
    
    if (slot.message && slot.message.trim()) {
      return slot.message;
    }
    
    const defaultMessages = {
      fully_booked: 'Désolé, ce créneau est complet. Veuillez choisir un autre horaire.',
      temporarily_closed: 'Nous sommes fermés à ce moment. Veuillez choisir un autre horaire.',
      resting_date: 'Nous sommes fermés ce jour. Veuillez choisir une autre date.'
    };
    
    return defaultMessages[slot.reason] || 'Ce créneau n\'est pas disponible. Veuillez choisir un autre horaire.';
  };

  // Formater la date pour l'affichage (multilingue)
  const formatDateDisplay = (date) => {
    const days = t('days') || [];
    const months = t('months') || [];
    const dayName = days[date.getDay()] || '';
    const day = date.getDate();
    const month = months[date.getMonth()] || '';
    return { dayName, day, month, fullDate: `${dayName} ${day} ${month}` };
  };

  const handleDateSelect = (date) => {
    const dateString = toLocalDateString(date);
    setFormData(prev => ({ ...prev, date: dateString, time: '' }));
    setBlockedMessage('');
  };

  const handleTimeSelect = (time) => {
    const dateString = formData.date;
    
    // Vérifier si le créneau est bloqué
    if (isSlotBlocked(dateString, time)) {
      const message = getBlockedMessage(dateString, time);
      setBlockedMessage(message);
      return; // Ne pas permettre la sélection
    }
    
    setFormData(prev => ({ ...prev, time }));
    setBlockedMessage('');
  };

  const handlePersonsSelect = (persons) => {
    setFormData(prev => ({ ...prev, persons }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Validation stricte: téléphone uniquement (8 chiffres max)
    if (name === 'emailOrPhone') {
      // Accepter uniquement les chiffres et limiter à 8
      if (/^\d*$/.test(value) && value.length <= 8) {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      } else if (value === '') {
        // Permettre la suppression
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Valider le numéro de téléphone (exactement 8 chiffres)
  const validatePhone = (value) => /^\d{8}$/.test(value || '');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;
    
    if (!formData.time) {
      alert(t('fillAllFields'));
      return;
    }
    
    // Vérifier si le créneau est bloqué
    if (isSlotBlocked(formData.date, formData.time)) {
      const message = getBlockedMessage(formData.date, formData.time);
      alert(message);
      return;
    }
    
    // Valider téléphone
    if (!validatePhone(formData.emailOrPhone)) {
      alert(t('invalidPhone'));
      return;
    }

    setIsSubmitting(true);
    
    // Tracker la réservation dans Analytics
    trackReservation({
      name: formData.name,
      date: formData.date,
      time: formData.time,
      persons: formData.persons
    });

    // Sauvegarder la réservation dans Firestore
    try {
      const reservationData = {
        name: formData.name || '',
        emailOrPhone: formData.emailOrPhone || '',
        date: formData.date,
        time: formData.time,
        persons: formData.persons || '2',
        note: formData.note || '',
        createdAt: Timestamp.now()
      };
      
      await addDoc(collection(db, 'reservations'), reservationData);
      console.log('✅ Réservation sauvegardée dans Firestore');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde dans Firestore:', error);
      // Continue même si la sauvegarde échoue
    }

    // Envoyer l'email avec les détails de la réservation (fonctionne automatiquement)
    try {
      await sendReservationEmail({
        name: formData.name,
        emailOrPhone: formData.emailOrPhone,
        date: formData.date,
        time: formData.time,
        persons: formData.persons,
        note: formData.note || ''
      });
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'email:', error);
      // Continue même si l'email échoue
    }

    // Sauvegarder les données de réservation
    setReservationData(formData);
    setSubmitted(true);
  };

  const selectedDateObj = dates.find(d => toLocalDateString(d) === formData.date);
  const selectedDateDisplay = selectedDateObj ? formatDateDisplay(selectedDateObj) : null;

  if (isSubmitting && !submitted) {
    return (
      <div className="reservation-page">
        <Header />
        <div className="reservation-container">
          <div className="submitting-message">
            <div className="submitting-spinner" />
            <h2>Soumission en cours...</h2>
            <p>Votre réservation est en train d'être envoyée. Merci de patienter.</p>
          </div>
        </div>
      </div>
    );
  }

  if (submitted && reservationData) {
    return (
      <div className="reservation-page">
        <Header />
        <div className="reservation-container">
          <div className="success-message">
            <h2>{t('reservationSuccess')}</h2>
            <p>{t('reservationConfirmation')}</p>
            <div className="reservation-details">
              {reservationData.name && <p><strong>{t('name')}:</strong> {reservationData.name}</p>}
              {reservationData.emailOrPhone && <p><strong>{t('phone')}:</strong> {reservationData.emailOrPhone}</p>}
              <p><strong>{t('date')}:</strong> {reservationData.date}</p>
              <p><strong>{t('time')}:</strong> {reservationData.time}</p>
              <p><strong>{t('persons')}:</strong> {reservationData.persons} {t('pax')}</p>
              {reservationData.note && <p><strong>{t('note')}:</strong> {reservationData.note}</p>}
            </div>
            <button onClick={() => {
              setSubmitted(false);
              setReservationData(null);
              setFormData({ name: '', emailOrPhone: '', date: getTodayDate(), time: '', persons: '2', note: '' });
            }} className="btn-primary">
              {t('newReservation')}
            </button>
            <button onClick={() => { trackMenuClick(); navigate('/menu'); }} className="btn-view-menu">
              {t('viewMenu')}
            </button>
            <button onClick={() => navigate('/')} className="btn-secondary">
              {t('backToHome')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reservation-page">
      <Header />
      <div className="reservation-container">
      <form onSubmit={handleSubmit} className="reservation-form">
        <fieldset disabled={isSubmitting} className="reservation-fieldset">
        <div className="reservation-header-inline">
          <button type="button" className="back-button" onClick={() => navigate('/')}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            type="button"
            className="btn-view-menu"
            onClick={() => { trackMenuClick(); navigate('/menu'); }}
          >
            {t('viewMenu')}
          </button>
        </div>
        {/* Name input */}
        <div className="form-section">
          <div className="form-section-header">
            <svg className="section-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
            <span className="section-label">{t('name')}:</span>
            <span className="section-value">{formData.name || t('enterName')}</span>
          </div>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder={t('enterName')}
            className="name-input"
          />
        </div>

        {/* Phone input */}
        <div className="form-section">
          <div className="form-section-header">
            <svg className="section-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
            <span className="section-label">{t('phone')}:</span>
            <span className="section-value">{formData.emailOrPhone || t('enterPhone')}</span>
          </div>
          <input
            type="tel"
            name="emailOrPhone"
            value={formData.emailOrPhone}
            onChange={handleChange}
            placeholder={t('enterPhone')}
            className="name-input"
            inputMode="numeric"
            autoComplete="tel"
          />
        </div>

        {/* Date Selection */}
        <div className="form-section">
          <div className="form-section-header">
            <svg className="section-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            <span className="section-label">{t('when')}:</span>
            <span className="section-value">
              {selectedDateDisplay ? selectedDateDisplay.fullDate : t('selectDate')}
            </span>
          </div>
          <div className="scrollable-row">
            {dates.map((date, index) => {
              const dateDisplay = formatDateDisplay(date);
              const isSelected = toLocalDateString(date) === formData.date;
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleDateSelect(date)}
                  className={`scrollable-button ${isSelected ? 'selected' : ''}`}
                >
                  {dateDisplay.dayName}<br />
                  {dateDisplay.day}
                </button>
              );
            })}
          </div>
        </div>

        {/* Time Selection */}
        <div className="form-section">
          <div className="form-section-header">
            <svg className="section-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            <span className="section-label">{t('time')}:</span>
            <span className="section-value">{formData.time || t('selectTime')}</span>
          </div>
          <div className="scrollable-row">
            {timeSlots
              .filter((time) => {
                // Si c'est aujourd'hui, filtrer les heures passées
                const isToday = formData.date === getTodayDate();
                if (isToday) {
                  const now = new Date();
                  const currentHour = now.getHours();
                  const currentMinute = now.getMinutes();
                  const [timeHour, timeMinute] = time.split(':').map(Number);
                  
                  // Ne pas afficher les heures passées
                  if (timeHour < currentHour || (timeHour === currentHour && timeMinute <= currentMinute)) {
                    return false;
                  }
                }
                return true;
              })
              .map((time, index) => {
                const isSelected = formData.time === time;
                const isBlocked = isSlotBlocked(formData.date, time);
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleTimeSelect(time)}
                    className={`scrollable-button ${isSelected ? 'selected' : ''} ${isBlocked ? 'blocked' : ''}`}
                    disabled={isBlocked}
                    title={isBlocked ? getBlockedMessage(formData.date, time) : ''}
                  >
                    {time}
                    {isBlocked && (
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: '4px' }}>
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                    )}
                  </button>
                );
              })}
          </div>
          {blockedMessage && (
            <div className="blocked-message">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {blockedMessage}
            </div>
          )}
        </div>

        {/* Number of People */}
        <div className="form-section">
          <div className="form-section-header">
            <svg className="section-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <span className="section-label">{t('howMany')}:</span>
            <span className="section-value">{formData.persons} {t('pax')}</span>
          </div>
          <div className="scrollable-row">
            {Array.from({ length: 60 }, (_, i) => i + 1).map((num) => {
              const isSelected = formData.persons === num.toString();
              return (
                <button
                  key={num}
                  type="button"
                  onClick={() => handlePersonsSelect(num.toString())}
                  className={`scrollable-button ${isSelected ? 'selected' : ''}`}
                >
                  {num} {t('pax')}
                </button>
              );
            })}
          </div>
        </div>

        {/* Note Section */}
        <div className="form-section">
          <div className="form-section-header">
            <span className="section-label">{t('note')}:</span>
          </div>
          <textarea
            name="note"
            value={formData.note}
            onChange={handleChange}
            placeholder={t('notePlaceholder')}
            className="note-input"
            rows="3"
          />
        </div>

        <button type="submit" className="btn-submit">
          {isSubmitting ? 'Soumission...' : t('finalizeReservation')}
        </button>
        </fieldset>
      </form>
      </div>
    </div>
  );
};

export default ReservationPage;
